import { ForwardedRef, MutableRefObject } from 'react'

import { getI18nText } from '~/src/shared/utils/translate'

import Axios from '@backoffice/services/Axios'

export const EXP_SEPARATOR_REGEX = /\.|,/g

export const copyToClipboard = async (value: string) => {
  if (navigator.clipboard) {
    return navigator.clipboard.writeText(value)
  }

  return new Promise((res, rej) => {
    const textarea = document.createElement('textarea')
    textarea.value = value

    textarea.readOnly = true

    textarea.style.position = 'fixed'
    textarea.style.opacity = '0'

    document.body.appendChild(textarea)
    textarea.focus()
    textarea.select()

    document.execCommand('copy') ? res(textarea.value) : rej()
    textarea.remove()
  })
}

export const cleanSeparator = (value: string, regExp = EXP_SEPARATOR_REGEX) => {
  if (!value) return null

  return value.replace(regExp, '')
}

export const delay = async (cb: () => void, time = 0) => {
  await new Promise((resolve) => setTimeout(resolve, time))

  cb()
}

export const getCSRF = () => {
  if (!document) throw new Error('CRSF: document expected')

  const element = document.querySelector('meta[name=csrf-token]') as HTMLMetaElement

  if (!element) throw new Error('CSRF: element not found')

  const csrfToken = element.content
  return csrfToken
}

export const redirect = (url: string) => {
  window.location.href = url
}

export const reloadPage = () => {
  // setTimeout is required to trigger the action on empty event loop
  setTimeout(() => {
    window.location.reload()
  }, 0)
}

export const getPluralTranslateId = (id) => {
  if (window.I18n.locale === 'zh-cn') return `${id}.other`

  return id
}

export const translateOption = <T>({ label, localePath, ...option }: LocaleOption & T) => ({
  ...option,
  label: getI18nText({
    id: localePath || '',
    defaultValue: label,
  }),
})

export const updateWindowState = (url: string) => {
  if (url !== window.location.href) {
    window.history.pushState('', '', url)
  }
}

export const assignForwardedRef = (ref: ForwardedRef<HTMLElement>, node: HTMLElement | null) => {
  if (!ref) return

  if (typeof ref === 'function') ref(node)

  const mutableRef = ref as MutableRefObject<HTMLElement>
  mutableRef.current = node as HTMLElement
}

export const scrollIntoInputError = (fieldName: string, options?: ScrollIntoViewOptions) => {
  const inputElement = document.querySelector(`input[name="${fieldName}"]`)?.parentElement

  inputElement?.scrollIntoView(options)
}

export const serverErrorInterceptor = (api: Axios, callback: () => void) => {
  api.appendResponseErrorInterceptor((error) => {
    if (error.response.status === 500) {
      callback()
    }

    return Promise.reject(error)
  })
}

export const setCaretPosition = (inputEl: HTMLInputElement, position: number) => {
  inputEl.setSelectionRange(position, position)
}

export const getOptionByValue = (options: SimpleOption[], value: string) =>
  options.find((option) => option?.value === value) || options[0]
